<template>
  <PageHeader
    title="Agent Statistics"
    breadcrumb
    :items="[
      { label: $t('ui.entities.report.label', 2), to: '/reports' },
      { label: $t('ui.entities.user.role.callAgent', 2) },
      { label: $t('ui.common.statistic', 2), to: '/reports/call-agents/statistics' }
    ]"
  >
    <template #right>
      <div class="flex items-center gap-3">
        <Select
          v-model="callCenterSelected"
          :options="callCentersAllowed"
          icon="headset"
          value-key="id"
          label-key="name"
          :placeholder="`Select a ${$t('ui.entities.departments.callCentre')}`"
          class="min-w-56"
          :readonly="callCentersAllowed.length == 1"
          @select="updateCallCenterSelected"
        ></Select>

        <DatePicker
          v-model="dateRange"
          selection-mode="range"
          show-presets
          class="min-w-60"
          :close-on-select="false"
          :presets="dateRangePresets"
          :months-visible="useBreakPoints({ xs: 1, sm: 2 })"
        />
        <div>
          <Button
            label="Update"
            :is-loading="isLoading"
            @click="onFetchData"
          />
        </div>
      </div>
    </template>
  </PageHeader>
  <Card>
    <DataTable
      ref="dt"
      v-model:filters="filters"
      export-filename="Agent Statistics"
      :value="agentStatistics"
      removable-sort
      :rows="100"
      data-key="id"
      column-resize-mode="fit"
      :paginator="true"
      responsive-layout="scroll"
      filter-display="menu"
      :rows-per-page-options="[10, 20, 50]"
      state-key="dt-agent-statistics"
      state-storage="session"
      :loading="isLoading"
      current-page-report-template="Showing {startRecord}-{endRecord} out of {totalRecords}"
    >
      <template #empty>
        <Empty
          v-if="!isLoading"
          :title="callCenterSelected ? 'No data' : `Select a ${$t('ui.entities.departments.callCentre')}`"
        />
      </template>

      <template #header>
        <div class="flex justify-between">
          <span class="flex justify-between gap-4">
            <span>
              <Input
                v-model="filters['global'].value"
                :placeholder="$t('ui.common.search', 1)"
                icon="search"
              />
            </span>
            <div class="toggle-container">
              <ToggleSwitch
                v-model="showConversionRates"
                size="sm"
              />
              <div class="font-medium">Show Conversion Rates</div>
            </div>
            <Button
              :label="$t('ui.actions.clear', 1)"
              severity="secondary"
              type="button"
              outlined
              icon="filter-slash"
              @click="clearFilters()"
            />
          </span>
        </div>
        <div class="flex justify-between mt-4">
          <span>
            <Button
              label="CSV"
              type="button"
              severity="secondary"
              outlined
              icon="file-excel"
              @click="exportCSV($event)"
            />
          </span>
        </div>
      </template>
      <Column
        field="userName"
        header="Name"
        hidden
      ></Column>

      <Column
        field="userId"
        header="Name"
        :sortable="true"
        :show-filter-match-modes="false"
        :show-filter-operator="false"
      >
        <template #body="{ data }">{{ data.userName }}</template>
        <template #filter="{ filterModel }">
          <Select
            v-model="filterModel.value"
            :options="usersList"
            mode="multiple"
            searchable
            :chips="false"
            value-key="value"
            label-key="label"
            placeholder="All"
            class="min-w-56"
          >
            <template #option="slotProps">
              <div class="flex items-center">
                <Avatar
                  :url="slotProps.option.profilePicUrl"
                  :text="slotProps.option.initials"
                  size="sm"
                  class="mr-3"
                />
                {{ slotProps.option.label }}
              </div>
            </template>
          </Select>
        </template>
      </Column>
      <Column
        field="productiveHours"
        header="Prod Hours"
        :sortable="true"
      >
        <template #body="{ data }">{{ data.productiveHours }}</template>
      </Column>

      <Column
        field="targetCalls"
        header="Call Target"
        :sortable="true"
      >
        <template #body="{ data }">{{ data.targetCalls }}</template>
      </Column>

      <Column
        field="stats.calls.initiated"
        header="Calls Initiated"
        :sortable="true"
      >
        <template #body="{ data }">{{ data.stats.calls.initiated }}</template>
      </Column>

      <Column
        field="stats.calls.attempted"
        header="Calls Attmpt"
        :sortable="true"
      >
        <template #body="{ data }">{{ data.stats.calls.attempted }}</template>
      </Column>

      <Column
        v-if="showConversionRates"
        :sortable="true"
      >
        <template #header>
          <div
            v-tippy="{ content: 'Calls Attempted VS Initiated' }"
            class="min-w-9"
          >
            %
            <Icon
              type="circle-info"
              fa-style="far"
            />
          </div>
        </template>
        <template #body="{ data }">
          <Chip
            v-tippy="{ content: 'Calls Attempted VS Initiated' }"
            :severity="getRagSeverity(Math.round(getAttemptedVsInitiatedRate(data) * 100), 90, 95)"
          >
            {{ numberFormatter(getAttemptedVsInitiatedRate(data) * 100, 'normal', 0) }}%
          </Chip>
        </template>
      </Column>

      <Column
        v-if="showConversionRates"
        field="stats.conversionRates.callsAttemptedVsTarget"
        :sortable="true"
      >
        <template #header>
          <div v-tippy="{ content: 'Calls Attempted VS Target' }">
            %
            <Icon
              type="circle-info"
              fa-style="far"
            />
          </div>
        </template>
        <template #body="{ data }">
          <Chip
            v-tippy="{ content: 'Calls Attempted VS Target' }"
            :severity="getRagSeverity(Math.round(data.stats.conversionRates.callsAttemptedVsTarget * 100), 95, 95)"
          >
            {{ numberFormatter(data.stats.conversionRates.callsAttemptedVsTarget * 100, 'normal', 0) }}%
          </Chip>
        </template>
      </Column>

      <Column
        field="stats.calls.averageLengthNoAnswer"
        header="Avg Length (No Answ)"
        :sortable="true"
      >
        <template #body="{ data }">
          {{ $dayjs.duration(data.stats.calls.averageLengthNoAnswer, 'seconds').format('mm:ss') }}
        </template>
      </Column>

      <Column
        field="stats.calls.answered"
        header="Calls Answ"
        :sortable="true"
      >
        <template #body="{ data }">
          {{ data.stats.calls.answered }}
        </template>
      </Column>

      <Column
        v-if="showConversionRates"
        field="stats.conversionRates.callsAnsweredVsAttempted"
        :sortable="true"
      >
        <template #header>
          <div
            v-tippy="{ content: 'Calls Answered VS Attempted' }"
            class="min-w-9"
          >
            %
            <Icon
              type="circle-info"
              fa-style="far"
            />
          </div>
        </template>
        <template #body="{ data }">
          <Chip
            v-tippy="{ content: 'Calls Answered VS Attempted' }"
            :severity="getRagSeverity(Math.round(data.stats.conversionRates.callsAnsweredVsAttempted * 100), 29, 29)"
          >
            {{ numberFormatter(data.stats.conversionRates.callsAnsweredVsAttempted * 100, 'normal', 0) }}%
          </Chip>
        </template>
      </Column>

      <Column
        field="stats.calls.opportunity"
        header="Oppt"
        :sortable="true"
      >
        <template #body="{ data }">
          {{ data.stats.calls.opportunity }}
        </template>
      </Column>

      <Column
        field="stats.calls.averageLengthOpportunity"
        header="Avg Length (Opp)"
        :sortable="true"
      >
        <template #body="{ data }">
          {{ $dayjs.duration(data.stats.calls.averageLengthOpportunity, 'seconds').format('mm:ss') }}
        </template>
      </Column>

      <Column
        v-if="showConversionRates"
        field="stats.conversionRates.opportunitiesVsAnswered"
        :sortable="true"
      >
        <template #header>
          <div
            v-tippy="{ content: 'Opportunity Calls VS Answered' }"
            class="min-w-9"
          >
            %
            <Icon
              type="circle-info"
              fa-style="far"
            />
          </div>
        </template>
        <template #body="{ data }">
          <Chip
            v-tippy="{ content: 'Opportunity Calls VS Answered' }"
            :severity="getRagSeverity(Math.round(data.stats.conversionRates.opportunitiesVsAnswered * 100), 62, 62)"
          >
            {{ numberFormatter(data.stats.conversionRates.opportunitiesVsAnswered * 100, 'normal', 0) }}%
          </Chip>
        </template>
      </Column>

      <Column
        field="stats.appointments.made"
        header="Appts Made"
        :sortable="true"
      >
        <template #body="{ data }">
          {{ data.stats.appointments.made }}
        </template>
      </Column>

      <Column
        v-if="showConversionRates"
        field="stats.conversionRates.opportunitiesVsApptsMade"
        :sortable="true"
      >
        <template #header>
          <div
            v-tippy="{ content: 'Opportunity Calls VS Appointments Made' }"
            class="min-w-9"
          >
            %
            <Icon
              type="circle-info"
              fa-style="far"
            />
          </div>
        </template>
        <template #body="{ data }">
          <Chip
            v-tippy="{ content: 'Opportunity Calls VS Appointments Made' }"
            :severity="getRagSeverity(Math.round(data.stats.conversionRates.opportunitiesVsApptsMade * 100), 95, 95)"
          >
            {{ numberFormatter(data.stats.conversionRates.opportunitiesVsApptsMade * 100, 'normal', 0) }}%
          </Chip>
        </template>
      </Column>

      <Column
        field="stats.appointments.occurring"
        header="Appts Occur"
        :sortable="true"
      >
        <template #body="{ data }">
          {{ data.stats.appointments.occurring }}
        </template>
      </Column>

      <Column
        field="stats.appointments.attended"
        header="Attd"
        :sortable="true"
      >
        <template #body="{ data }">
          {{ data.stats.appointments.attended }}
        </template>
      </Column>

      <Column
        v-if="showConversionRates"
        field="stats.conversionRates.attendedVsOccurring"
        :sortable="true"
      >
        <template #header>
          <div
            v-tippy="{ content: 'Attended VS Occurring' }"
            class="min-w-9"
          >
            %
            <Icon
              type="circle-info"
              fa-style="far"
            />
          </div>
        </template>
        <template #body="{ data }">
          <Chip
            v-tippy="{ content: 'Attended VS Occurring' }"
            :severity="getRagSeverity(Math.round(data.stats.conversionRates.attendedVsOccurring * 100), 95, 95)"
          >
            {{ numberFormatter(data.stats.conversionRates.attendedVsOccurring * 100, 'normal', 0) }}%
          </Chip>
        </template>
      </Column>

      <ColumnGroup type="footer">
        <Row>
          <Column
            :colspan="1"
            footer-style="text-align:left; padding: 14px 21px;"
          >
            <template #footer>
              {{ $t('ui.common.total', 1) }}
            </template>
          </Column>
          <Column footer-style="padding: 14px 21px;">
            <template v-if="tableData.length">
              {{ tableData.map(d => d.productiveHours || 0).reduce((a, b) => a + b, 0) }}
            </template>
          </Column>
          <Column footer-style="padding: 14px 21px;">
            <template
              v-if="tableData.length"
              #footer
            >
              {{ tableData.map(d => d.stats.calls.initiated || 0).reduce((a, b) => a + b, 0) }}
            </template>
          </Column>
          <Column footer-style="padding: 14px 21px;">
            <template
              v-if="tableData.length"
              #footer
            >
              {{ tableData.map(d => d.targetCalls || 0).reduce((a, b) => a + b, 0) }}
            </template>
          </Column>
          <Column footer-style="padding: 14px 21px;">
            <template
              v-if="tableData.length"
              #footer
            >
              {{ tableData.map(d => d.stats.calls.attempted || 0).reduce((a, b) => a + b, 0) }}
            </template>
          </Column>
          <Column
            v-if="showConversionRates"
            footer-style="padding: 14px 21px;"
          ></Column>
          <Column
            v-if="showConversionRates"
            footer-style="padding: 14px 21px;"
          ></Column>
          <Column footer-style="padding: 14px 21px;"></Column>
          <Column footer-style="padding: 14px 21px;">
            <template
              v-if="tableData.length"
              #footer
            >
              {{ tableData.map(d => d.stats.calls.answered || 0).reduce((a, b) => a + b, 0) }}
            </template>
          </Column>
          <Column
            v-if="showConversionRates"
            footer-style="padding: 14px 21px;"
          ></Column>
          <Column footer-style="padding: 14px 21px;">
            <template
              v-if="tableData.length"
              #footer
            >
              {{ tableData.map(d => d.stats.calls.opportunity || 0).reduce((a, b) => a + b, 0) }}
            </template>
          </Column>
          <Column footer-style="padding: 14px 21px;"></Column>
          <Column
            v-if="showConversionRates"
            footer-style="padding: 14px 21px;"
          ></Column>
          <Column footer-style="padding: 14px 21px;">
            <template
              v-if="tableData.length"
              #footer
            >
              {{ tableData.map(d => d.stats.appointments.made || 0).reduce((a, b) => a + b, 0) }}
            </template>
          </Column>
          <Column
            v-if="showConversionRates"
            footer-style="padding: 14px 21px;"
          ></Column>
          <Column footer-style="padding: 14px 21px;">
            <template
              v-if="tableData.length"
              #footer
            >
              {{ tableData.map(d => d.stats.appointments.occurring || 0).reduce((a, b) => a + b, 0) }}
            </template>
          </Column>
          <Column footer-style="padding: 14px 21px;">
            <template
              v-if="tableData.length"
              #footer
            >
              {{ tableData.map(d => d.stats.appointments.attended || 0).reduce((a, b) => a + b, 0) }}
            </template>
          </Column>
        </Row>
      </ColumnGroup>
      <Column
        v-if="showConversionRates"
        footer-style="padding: 14px 21px;"
      ></Column>
    </DataTable>
  </Card>
</template>

<script setup>
import Card from '@/components/card/Card.vue'
import PageHeader from '@/components/page/PageHeader.vue'
import DatePicker from '@/components/forms/DatePicker.vue'
import Icon from '@/components/icon/Icon.vue'
import Input from '@/components/forms/Input.vue'
import Button from '@/components/button/Button.vue'
import Chip from '@/components/chip/Chip.vue'
import ToggleSwitch from '@/components/forms/ToggleSwitch.vue'
import Select from '@/components/forms/Select.vue'
import Empty from '@/components/empty/Empty.vue'
import Avatar from '@/components/avatar/Avatar.vue'

import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import ColumnGroup from 'primevue/columngroup'
import Row from 'primevue/row'
import { FilterMatchMode } from 'primevue/api'

import dayjs from 'dayjs'

import useApiRequest from '@/composables/useApiRequest'
import { useBreakPoints } from '@/composables/useWindowSize'
import { useUserStore } from '@/stores/UserStore.ts'

import { ref, computed } from 'vue'
import { alertToast } from '@/utilities/notification'
import { getRagSeverity } from '@/utilities'

const dt = ref({ processedData: [] })
const userStore = useUserStore()

const dateRange = ref([dayjs().startOf('month').format('YYYY-MM-DD'), dayjs().format('YYYY-MM-DD')])
const dateRangePresets = [
  {
    label: 'This Week',
    value: {
      start: dayjs().startOf('week'),
      end: dayjs().endOf('week')
    }
  },
  {
    label: 'This Month',
    value: {
      start: dayjs().startOf('month'),
      end: dayjs().endOf('month')
    }
  },
  {
    label: 'Last Month',
    value: {
      start: dayjs().subtract(1, 'month').startOf('month'),
      end: dayjs().subtract(1, 'month').endOf('month')
    }
  }
]

const statisticsApiRequest = useApiRequest()
const isLoading = statisticsApiRequest.isLoading
const agentStatistics = ref([])

const showConversionRates = ref(false)
const users = ref([])
const filters = ref([])

const callCentersAllowed = userStore.callCenters
const callCenterSelected = ref(null)

function initFilters() {
  filters.value = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    userId: { value: null, matchMode: FilterMatchMode.IN }
  }
}
initFilters()

function clearFilters() {
  initFilters()
}

function fetchStatistics() {
  try {
    statisticsApiRequest
      .send({
        endpoint: '/v1/reports/call-agents/statistics',
        params: {
          callCenterId: callCenterSelected.value,
          start: dayjs(dateRange.value[0]).format('YYYY-MM-DD'),
          end: dayjs(dateRange.value[1]).format('YYYY-MM-DD')
        }
      })
      .then(response => {
        if (response.success) {
          agentStatistics.value = response.data
        } else {
          alertToast('Failed to fetch data', response.message, 'error')
        }
      })
  } catch (err) {
    alertToast('Failed to fetch data', err.message, 'error')
  }
}

if (!localStorage.getItem('report-filter-call-center-selected')) {
  callCenterSelected.value = callCentersAllowed[0].id
} else {
  callCenterSelected.value = parseInt(localStorage.getItem('report-filter-call-center-selected'))
}

fetchStatistics()

function onFetchData() {
  if (dateRange.value[0] && dateRange.value[1] && callCenterSelected.value) {
    fetchStatistics()
  }
}

function updateCallCenterSelected() {
  localStorage.setItem('report-filter-call-center-selected', callCenterSelected.value)
}

// Users List
const usersApiRequest = useApiRequest()
usersApiRequest.send({ endpoint: '/v1/users?userType=retain' }).then(response => {
  users.value = response.data
})

const usersList = computed(() => {
  // if no users, return empty array
  if (!users.value) return []

  return users.value.map(u => {
    return {
      value: u.id,
      label: u.fullName,
      profilePicUrl: u.profilePicUrl,
      initials: u.initials
    }
  })
})

const tableData = computed(() => {
  return dt.value.processedData
})

/**
 * Calculates the conversion rate from calls initiated to calls attempted.
 * Returns 0 if calls initiated is 0 to avoid division by zero.
 *
 * @param data - The agent statistics data object
 * @returns The conversion rate as a decimal (0-1)
 */
const getAttemptedVsInitiatedRate = data => {
  const initiated = data.stats?.calls?.initiated || 0
  const attempted = data.stats?.calls?.attempted || 0

  if (initiated === 0) {
    return 0
  }

  return attempted / initiated
}

const exportCSV = () => {
  // TODO: create a custom csv composable that can be passed a header, rows and footer.
  // Reason for this is that primevuew has no way to define the key to be used for the column. eg we want createdByName to be used for createdBy
  // For now, we will use the default exportCSV method for simplicity.

  dt.value.exportCSV()
}
</script>

<style>
.toggle-container {
  border: 1px solid var(--border-secondary);
  border-radius: var(--rounded-md);
  padding: var(--s-2) var(--s-3) var(--s-2) var(--s-2);
  text-align: center;
  display: flex;
  align-items: center;
  gap: 10px;
  color: var(--text-secondary);
}
</style>
